/**
 * Storefront themes and landing-section variants (KAN-1546, KAN-1547).
 *
 * The one place that defines what a tenant can pick for the look of its
 * public website. Values are taken 1:1 from the Claude Design handoff
 * „Web Temata" (project fd0495a1…, files ``web-temata-data.js`` and
 * ``web-temata.js``).
 *
 * A theme changes only the look — fonts, radii, shadows, neutral surfaces,
 * header, buttons, density. The tenant's brand colour stays the tenant's:
 * the theme derives the accent tokens from it with a contrast check, so
 * any brand colour works with any theme, in light and dark mode.
 *
 * The backend reads the generated copy in
 * ``apps/api/app/data/site_themes.json`` (theme keys + section variants)
 * to validate what a tenant saves; ``tests/siteThemes.test.ts`` fails when
 * the two drift and rewrites the file with ``-u``.
 */

export const SECTION_VARIANTS = {
  hero: ["A", "B", "C"],
  steps: ["A", "B"],
  benefits: ["A", "B"],
  // The hero band of every sub-page (KAN-1636): A left, B centred,
  // C compact. One choice for all sub-pages, so they read as one site.
  page_hero: ["A", "B", "C"],
} as const;

export type SiteSection = keyof typeof SECTION_VARIANTS;
export type SectionVariant<S extends SiteSection = SiteSection> =
  (typeof SECTION_VARIANTS)[S][number];
export type SectionVariants = { [S in SiteSection]: SectionVariant<S> };

/** Layout of the landing when no theme is chosen — the page as it was. */
export const DEFAULT_SECTION_VARIANTS: SectionVariants = {
  hero: "A",
  steps: "A",
  benefits: "A",
  page_hero: "A",
};

type Mode = "light" | "dark";
interface Neutrals {
  bg: string;
  surface: string;
  surface2: string;
  border: string;
  borderStrong: string;
}
type Tint = { [K in keyof Neutrals]: [base: string, amount: number] };
interface Chrome {
  bg: string;
  ink: string;
  ink2: string;
}

export interface SiteTheme {
  key: string;
  nameKey: string;
  forKey: string;
  font: string;
  radii: [xs: string, sm: string, md: string, lg: string];
  shadow: Record<Mode, [sm: string, md: string, lg: string]>;
  /** Fixed neutrals, or — ``tint`` — neutrals mixed with the brand colour. */
  neutrals?: Record<Mode, Neutrals>;
  tint?: Record<Mode, Tint>;
  header: Record<Mode, Chrome & { border: string }>;
  cta: Record<Mode, Chrome>;
  button: { h: string; weight: number; r: string; tt: string; ls: string; fs: string };
  type: { h1: string; h1ls: string; weight: number; h2: string; h2ls: string };
  sectionPad: string;
  heroPad: string;
  /** Section layouts the theme starts with; the tenant may change them. */
  variants: SectionVariants;
  /** Hero and final CTA stay dark in both modes. */
  heroDark?: true;
}

const INK: Record<Mode, { ink: string; ink2: string; ink3: string }> = {
  light: { ink: "#0c0a09", ink2: "#3f3b37", ink3: "#6d6862" },
  dark: { ink: "#fffefd", ink2: "#eeeae3", ink3: "#cbc3b7" },
};

const FONT_MONO = "'Geist Mono', ui-monospace, Menlo, Consolas, monospace";

export const SITE_THEMES = [
  {
    key: "prumysl",
    nameKey: "admin:siteAppearance.themes.prumysl.name",
    forKey: "admin:siteAppearance.themes.prumysl.for",
    font: "'IBM Plex Sans', system-ui, sans-serif",
    radii: ["2px", "2px", "3px", "4px"],
    shadow: {
      light: [
        "none",
        "0 1px 0 rgba(15,17,19,.06)",
        "0 0 0 1px rgba(15,17,19,.08), 0 12px 24px -12px rgba(15,17,19,.25)",
      ],
      dark: [
        "none",
        "0 1px 0 rgba(0,0,0,.5)",
        "0 0 0 1px rgba(0,0,0,.6), 0 16px 32px -12px rgba(0,0,0,.8)",
      ],
    },
    neutrals: {
      light: { bg: "#eceeef", surface: "#ffffff", surface2: "#f5f6f7", border: "#cdd2d6", borderStrong: "#8e969e" },
      dark: { bg: "#0f1113", surface: "#191c1f", surface2: "#22262a", border: "#353b41", borderStrong: "#5b646d" },
    },
    header: {
      light: { bg: "#15171a", ink: "#ffffff", ink2: "#c5cbd1", border: "#15171a" },
      dark: { bg: "#191c1f", ink: "#ffffff", ink2: "#c5cbd1", border: "#353b41" },
    },
    cta: {
      light: { bg: "#15171a", ink: "#ffffff", ink2: "#c5cbd1" },
      dark: { bg: "#22262a", ink: "#ffffff", ink2: "#c5cbd1" },
    },
    button: { h: "46px", weight: 600, r: "2px", tt: "uppercase", ls: ".05em", fs: "13px" },
    type: { h1: "60px", h1ls: "-0.02em", weight: 600, h2: "34px", h2ls: "-0.01em" },
    sectionPad: "88px",
    heroPad: "72px",
    variants: { hero: "B", benefits: "A", steps: "A", page_hero: "A" },
  },
  {
    key: "studio",
    nameKey: "admin:siteAppearance.themes.studio.name",
    forKey: "admin:siteAppearance.themes.studio.for",
    font: "'Bricolage Grotesque Variable', system-ui, sans-serif",
    radii: ["8px", "12px", "20px", "28px"],
    shadow: {
      light: [
        "0 1px 2px rgba(24,20,16,.06)",
        "0 10px 30px -12px rgba(24,20,16,.18), 0 2px 4px rgba(24,20,16,.04)",
        "0 30px 60px -20px rgba(24,20,16,.28)",
      ],
      dark: [
        "0 1px 2px rgba(0,0,0,.6)",
        "0 10px 30px -12px rgba(0,0,0,.8), 0 2px 4px rgba(0,0,0,.5)",
        "0 30px 60px -20px rgba(0,0,0,.9)",
      ],
    },
    neutrals: {
      light: { bg: "#faf9f7", surface: "#ffffff", surface2: "#f2f0ec", border: "#e6e2dc", borderStrong: "#c9c3b9" },
      dark: { bg: "#111111", surface: "#1b1b1b", surface2: "#252525", border: "#323232", borderStrong: "#4d4d4d" },
    },
    header: {
      light: { bg: "transparent", ink: "var(--ink)", ink2: "var(--ink-2)", border: "transparent" },
      dark: { bg: "transparent", ink: "var(--ink)", ink2: "var(--ink-2)", border: "transparent" },
    },
    cta: {
      light: { bg: "var(--accent)", ink: "var(--on-accent)", ink2: "var(--on-accent)" },
      dark: { bg: "var(--accent)", ink: "var(--on-accent)", ink2: "var(--on-accent)" },
    },
    button: { h: "48px", weight: 600, r: "999px", tt: "none", ls: "0", fs: "15px" },
    type: { h1: "78px", h1ls: "-0.04em", weight: 700, h2: "42px", h2ls: "-0.03em" },
    sectionPad: "104px",
    heroPad: "88px",
    variants: { hero: "A", benefits: "A", steps: "B", page_hero: "B" },
  },
  {
    key: "tradice",
    nameKey: "admin:siteAppearance.themes.tradice.name",
    forKey: "admin:siteAppearance.themes.tradice.for",
    // System serif — nothing is downloaded.
    font: "Charter, 'Bitstream Charter', 'Sitka Text', Cambria, Georgia, serif",
    radii: ["2px", "3px", "4px", "6px"],
    shadow: {
      light: [
        "0 1px 0 rgba(70,50,20,.06)",
        "0 2px 8px -2px rgba(70,50,20,.12)",
        "0 16px 36px -16px rgba(70,50,20,.30)",
      ],
      dark: [
        "0 1px 0 rgba(0,0,0,.5)",
        "0 2px 8px -2px rgba(0,0,0,.7)",
        "0 16px 36px -16px rgba(0,0,0,.85)",
      ],
    },
    neutrals: {
      light: { bg: "#f5f0e6", surface: "#fffdf8", surface2: "#f1ebe0", border: "#dcd1bc", borderStrong: "#b3a386" },
      dark: { bg: "#15120e", surface: "#1f1b16", surface2: "#29241d", border: "#3d362c", borderStrong: "#5e5344" },
    },
    header: {
      light: { bg: "var(--surface)", ink: "var(--ink)", ink2: "var(--ink-2)", border: "var(--border-strong)" },
      dark: { bg: "var(--surface)", ink: "var(--ink)", ink2: "var(--ink-2)", border: "var(--border-strong)" },
    },
    cta: {
      light: { bg: "var(--surface-2)", ink: "var(--ink)", ink2: "var(--ink-2)" },
      dark: { bg: "var(--surface-2)", ink: "var(--ink)", ink2: "var(--ink-2)" },
    },
    button: { h: "44px", weight: 600, r: "3px", tt: "none", ls: "0", fs: "15px" },
    type: { h1: "64px", h1ls: "-0.015em", weight: 400, h2: "38px", h2ls: "-0.01em" },
    sectionPad: "96px",
    heroPad: "80px",
    variants: { hero: "A", benefits: "B", steps: "B", page_hero: "B" },
  },
  {
    key: "presnost",
    nameKey: "admin:siteAppearance.themes.presnost.name",
    forKey: "admin:siteAppearance.themes.presnost.for",
    font: "'Geist Sans', system-ui, sans-serif",
    radii: ["2px", "4px", "6px", "8px"],
    shadow: {
      light: ["none", "0 1px 2px rgba(15,23,42,.06)", "0 16px 40px -16px rgba(15,23,42,.22)"],
      dark: ["none", "0 1px 2px rgba(0,0,0,.6)", "0 16px 40px -16px rgba(0,0,0,.85)"],
    },
    neutrals: {
      light: { bg: "#f6f7f9", surface: "#ffffff", surface2: "#eef1f4", border: "#dde2e8", borderStrong: "#aab3be" },
      dark: { bg: "#0a0c0f", surface: "#12161b", surface2: "#1a1f26", border: "#29313b", borderStrong: "#46505d" },
    },
    header: {
      light: { bg: "transparent", ink: "var(--ink)", ink2: "var(--ink-2)", border: "var(--border)" },
      dark: { bg: "transparent", ink: "var(--ink)", ink2: "var(--ink-2)", border: "var(--border)" },
    },
    cta: {
      light: { bg: "#0b0d10", ink: "#ffffff", ink2: "#b7c0cb" },
      dark: { bg: "var(--surface-2)", ink: "var(--ink)", ink2: "var(--ink-2)" },
    },
    button: { h: "40px", weight: 500, r: "4px", tt: "none", ls: "0", fs: "14px" },
    type: { h1: "54px", h1ls: "-0.035em", weight: 600, h2: "30px", h2ls: "-0.025em" },
    sectionPad: "72px",
    heroPad: "56px",
    variants: { hero: "C", benefits: "B", steps: "A", page_hero: "C" },
  },
  {
    key: "uhel",
    nameKey: "admin:siteAppearance.themes.uhel.name",
    forKey: "admin:siteAppearance.themes.uhel.for",
    font: "'Instrument Sans Variable', system-ui, sans-serif",
    radii: ["4px", "6px", "10px", "12px"],
    shadow: {
      light: [
        "0 1px 2px rgba(12,12,11,.06)",
        "0 8px 24px -10px rgba(12,12,11,.18), 0 1px 2px rgba(12,12,11,.05)",
        "0 24px 60px -20px rgba(12,12,11,.45)",
      ],
      dark: [
        "0 1px 2px rgba(0,0,0,.6)",
        "0 8px 24px -10px rgba(0,0,0,.8)",
        "0 24px 60px -20px rgba(0,0,0,.9)",
      ],
    },
    neutrals: {
      light: { bg: "#f4f4f2", surface: "#ffffff", surface2: "#eeeeeb", border: "#deded9", borderStrong: "#b3b3ad" },
      dark: { bg: "#0d0d0c", surface: "#171716", surface2: "#20201e", border: "#30302d", borderStrong: "#52524d" },
    },
    header: {
      light: { bg: "transparent", ink: "#ffffff", ink2: "#d6d3cf", border: "rgba(255,255,255,.1)" },
      dark: { bg: "transparent", ink: "#ffffff", ink2: "#d6d3cf", border: "rgba(255,255,255,.1)" },
    },
    cta: {
      light: { bg: "var(--hero-bg)", ink: "#ffffff", ink2: "#d6d3cf" },
      dark: { bg: "var(--hero-bg)", ink: "#ffffff", ink2: "#d6d3cf" },
    },
    button: { h: "46px", weight: 600, r: "8px", tt: "none", ls: "0", fs: "15px" },
    type: { h1: "68px", h1ls: "-0.035em", weight: 600, h2: "38px", h2ls: "-0.025em" },
    sectionPad: "96px",
    heroPad: "80px",
    variants: { hero: "B", benefits: "A", steps: "A", page_hero: "A" },
    heroDark: true,
  },
  {
    key: "svezi",
    nameKey: "admin:siteAppearance.themes.svezi.name",
    forKey: "admin:siteAppearance.themes.svezi.for",
    font: "'Manrope Variable', system-ui, sans-serif",
    radii: ["8px", "12px", "16px", "20px"],
    shadow: {
      light: [
        "0 1px 2px color-mix(in srgb, var(--brand) 14%, transparent)",
        "0 12px 30px -14px color-mix(in srgb, var(--brand) 40%, transparent)",
        "0 28px 60px -24px color-mix(in srgb, var(--brand) 50%, transparent)",
      ],
      dark: [
        "0 1px 2px rgba(0,0,0,.6)",
        "0 12px 30px -14px rgba(0,0,0,.8)",
        "0 28px 60px -24px rgba(0,0,0,.9)",
      ],
    },
    tint: {
      light: {
        bg: ["#ffffff", 0.045],
        surface: ["#ffffff", 0],
        surface2: ["#ffffff", 0.085],
        border: ["#e3e3e3", 0.18],
        borderStrong: ["#b5b5b5", 0.25],
      },
      dark: {
        bg: ["#0c0c0d", 0.06],
        surface: ["#151516", 0.08],
        surface2: ["#1e1e20", 0.1],
        border: ["#2c2c2f", 0.14],
        borderStrong: ["#4a4a4f", 0.18],
      },
    },
    header: {
      light: { bg: "var(--surface)", ink: "var(--ink)", ink2: "var(--ink-2)", border: "var(--border)" },
      dark: { bg: "var(--surface)", ink: "var(--ink)", ink2: "var(--ink-2)", border: "var(--border)" },
    },
    cta: {
      light: { bg: "var(--accent-bg)", ink: "var(--ink)", ink2: "var(--ink-2)" },
      dark: { bg: "var(--accent-bg)", ink: "var(--ink)", ink2: "var(--ink-2)" },
    },
    button: { h: "48px", weight: 700, r: "12px", tt: "none", ls: "0", fs: "15px" },
    type: { h1: "64px", h1ls: "-0.035em", weight: 800, h2: "38px", h2ls: "-0.03em" },
    sectionPad: "96px",
    heroPad: "72px",
    variants: { hero: "C", benefits: "A", steps: "B", page_hero: "C" },
  },
] as const satisfies readonly SiteTheme[];

export type SiteThemeKey = (typeof SITE_THEMES)[number]["key"];

export function findSiteTheme(key: string | null | undefined): SiteTheme | null {
  return SITE_THEMES.find((theme) => theme.key === key) ?? null;
}

/** Layout a storefront renders: the tenant's choice over the theme's start. */
export function effectiveSectionVariants(
  theme: SiteTheme | null,
  chosen: Partial<Record<string, string>> | null | undefined,
): SectionVariants {
  const base = theme?.variants ?? DEFAULT_SECTION_VARIANTS;
  const out = { ...base };
  for (const section of Object.keys(SECTION_VARIANTS) as SiteSection[]) {
    const value = chosen?.[section];
    if (value && (SECTION_VARIANTS[section] as readonly string[]).includes(value)) {
      (out as Record<string, string>)[section] = value;
    }
  }
  return out;
}

// ---------------------------------------------------------------------------
// Colour derivation — ported from the handoff (``derive`` in web-temata.js).

const HEX_RE = /^#?([0-9a-f]{6})$/i;

/** Brand used when the tenant set none — the platform's default blue. */
export const DEFAULT_BRAND = "#2563eb";

function rgb(hex: string): [number, number, number] {
  const h = hex.replace("#", "");
  return [0, 2, 4].map((i) => Number.parseInt(h.slice(i, i + 2), 16)) as [
    number,
    number,
    number,
  ];
}

function toHex(values: number[]): string {
  return `#${values
    .map((v) => Math.round(Math.max(0, Math.min(255, v))).toString(16).padStart(2, "0"))
    .join("")}`;
}

function mix(a: string, b: string, t: number): string {
  const A = rgb(a);
  const B = rgb(b);
  return toHex(A.map((v, i) => v + (B[i]! - v) * t));
}

function luminance(hex: string): number {
  const [r, g, b] = rgb(hex).map((value) => {
    const v = value / 255;
    return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4;
  }) as [number, number, number];
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

export function contrast(a: string, b: string): number {
  const x = luminance(a);
  const y = luminance(b);
  return (Math.max(x, y) + 0.05) / (Math.min(x, y) + 0.05);
}

export function normaliseBrand(input: string | null | undefined): string {
  const match = input ? HEX_RE.exec(input.trim()) : null;
  return match ? `#${match[1]!.toLowerCase()}` : DEFAULT_BRAND;
}

interface AccentTokens {
  fill: string;
  on: string;
  soft: string;
  text: string;
  borderSoft: string;
}

/**
 * Accent tokens for one brand colour against one set of neutrals.
 *
 * - fill (buttons): the brand; if white text misses 4.5:1 it darkens by up
 *   to 8 %, else the text on it turns dark.
 * - text (links, icons): darkened (light) / lightened (dark mode) until it
 *   holds 4.5:1 against bg, surface, surface-2 and the soft tint.
 * - soft: 10 % (light) / 20 % (dark) of the brand in the surface.
 */
export function deriveAccent(brand: string, mode: Mode, n: Neutrals): AccentTokens {
  let fill = brand;
  let on = "#ffffff";
  if (contrast(brand, "#ffffff") < 4.5) {
    let ok = false;
    for (let t = 0.02; t <= 0.081; t += 0.02) {
      const candidate = mix(brand, "#000000", t);
      if (contrast(candidate, "#ffffff") >= 4.5) {
        fill = candidate;
        ok = true;
        break;
      }
    }
    if (!ok) {
      fill = brand;
      on = "#0c0a09";
    }
  }
  const soft = mix(n.surface, brand, mode === "light" ? 0.1 : 0.2);
  const targets = [n.bg, n.surface, n.surface2, soft];
  let t = 0;
  let text = brand;
  while (Math.min(...targets.map((x) => contrast(text, x))) < 4.5 && t < 1) {
    t += 0.03;
    text = mix(brand, mode === "light" ? "#000000" : "#ffffff", t);
  }
  return {
    fill,
    on,
    soft,
    text,
    borderSoft: mix(n.surface, brand, mode === "light" ? 0.35 : 0.5),
  };
}

export function themeNeutrals(theme: SiteTheme, mode: Mode, brand: string): Neutrals {
  if (theme.neutrals) return theme.neutrals[mode];
  const tint = theme.tint![mode];
  return {
    bg: mix(tint.bg[0], brand, tint.bg[1]),
    surface: mix(tint.surface[0], brand, tint.surface[1]),
    surface2: mix(tint.surface2[0], brand, tint.surface2[1]),
    border: mix(tint.border[0], brand, tint.border[1]),
    borderStrong: mix(tint.borderStrong[0], brand, tint.borderStrong[1]),
  };
}

function modeVars(theme: SiteTheme, mode: Mode, brand: string): Record<string, string> {
  const n = themeNeutrals(theme, mode, brand);
  const ink = INK[mode];
  const a = deriveAccent(brand, mode, n);
  const header = theme.header[mode];
  const cta = theme.cta[mode];
  const shadow = theme.shadow[mode];
  const heroBg =
    theme.key === "studio"
      ? mix(n.bg, brand, mode === "light" ? 0.07 : 0.06)
      : theme.heroDark
        ? mix("#121211", brand, 0.1)
        : n.bg;
  return {
    "--brand": brand,
    "--font-sans": theme.font,
    "--font-mono": FONT_MONO,
    "--r-xs": theme.radii[0],
    "--r-sm": theme.radii[1],
    "--r-md": theme.radii[2],
    "--r-lg": theme.radii[3],
    "--shadow-sm": shadow[0],
    "--shadow-md": shadow[1],
    "--shadow-lg": shadow[2],
    "--bg": n.bg,
    "--surface": n.surface,
    "--surface-2": n.surface2,
    "--border": n.border,
    "--border-strong": n.borderStrong,
    "--ink": ink.ink,
    "--ink-2": ink.ink2,
    "--ink-3": ink.ink3,
    // The app's accent tokens: ``--accent`` fills buttons, ``--accent-ink``
    // is accent-coloured text, ``--accent-bg`` the soft tint.
    "--accent": a.fill,
    "--on-accent": a.on,
    "--accent-ink": a.text,
    "--accent-bg": a.soft,
    "--accent-border": a.borderSoft,
    "--st-hdr-bg": header.bg,
    "--st-hdr-ink": header.ink,
    "--st-hdr-ink2": header.ink2,
    "--st-hdr-bd": header.border,
    "--st-cta-bg": cta.bg,
    "--st-cta-ink": cta.ink,
    "--st-cta-ink2": cta.ink2,
    "--hero-bg": heroBg,
    "--st-grid": mode === "light" ? "rgba(15,23,42,.05)" : "rgba(255,255,255,.045)",
    "--st-btn-h": theme.button.h,
    "--st-btn-w": String(theme.button.weight),
    "--st-btn-r": theme.button.r,
    "--st-btn-tt": theme.button.tt,
    "--st-btn-ls": theme.button.ls,
    "--st-btn-fs": theme.button.fs,
    "--st-h1": theme.type.h1,
    "--st-h1ls": theme.type.h1ls,
    "--st-hw": String(theme.type.weight),
    "--st-h2": theme.type.h2,
    "--st-h2ls": theme.type.h2ls,
    "--st-sec-pad": theme.sectionPad,
    "--st-hero-pad": theme.heroPad,
  };
}

/**
 * Replace references to other tokens by their values for this mode, so a
 * scope that re-declares ``--ink`` can say "the header ink is the page
 * ink" without a cycle.
 */
function resolveRefs(value: string, refs: Record<string, string>): string {
  return value.replace(/var\((--[a-z0-9-]+)\)/g, (whole, name: string) => refs[name] ?? whole);
}

/**
 * Tokens for a dark band of the page (a dark header, the always-dark hero
 * of a ``heroDark`` theme): its own surfaces and ink, so the Tailwind
 * utilities inside it — text, dropdown panels, buttons — stay readable.
 */
function darkScopeVars(background: string, brand: string): Record<string, string> {
  const hb = background;
  const n: Neutrals = {
    bg: hb,
    surface: mix(hb, "#ffffff", 0.05),
    surface2: mix(hb, "#ffffff", 0.09),
    border: mix(hb, "#ffffff", 0.14),
    borderStrong: mix(hb, "#ffffff", 0.3),
  };
  const a = deriveAccent(brand, "dark", n);
  return {
    "--ink": "#ffffff",
    "--ink-2": "#e2dfdb",
    "--ink-3": "#a8a49e",
    "--surface": n.surface,
    "--surface-2": n.surface2,
    "--border": n.border,
    "--border-strong": n.borderStrong,
    "--surface-3": mix(hb, "#ffffff", 0.13),
    "--ink-4": "#aba398",
    "--ink-5": "#605c57",
    "--accent-bg": a.soft,
    "--accent-ink": a.text,
    "--accent-border": a.borderSoft,
    // Status tones from the kit's dark palette — a warning box inside a
    // dark band must not keep its light background under white ink.
    ...DARK_STATUS,
    "color-scheme": "dark",
  };
}

// Ingot ``:root.dark`` status tokens (tokens.generated.css).
const DARK_STATUS: Record<string, string> = {
  "--ok": "#22c55e",
  "--ok-bg": "#0a2013",
  "--ok-border": "#166534",
  "--warn": "#f59e0b",
  "--warn-bg": "#2a1e07",
  "--warn-border": "#92400e",
  "--danger": "#f36464",
  "--danger-bg": "#2a1010",
  "--danger-border": "#991b1b",
};

const block = (selector: string, vars: Record<string, string>) =>
  `${selector}{${Object.entries(vars)
    .map(([k, v]) => `${k}:${v}`)
    .join(";")}}`;

/** Header tokens: its background and — when it is dark — a dark scope. */
function headerVars(theme: SiteTheme, mode: Mode, brand: string): Record<string, string> {
  const vars = modeVars(theme, mode, brand);
  const header = theme.header[mode];
  const heroBg = vars["--hero-bg"]!;
  const bg = header.bg === "transparent" ? heroBg : resolveRefs(header.bg, vars);
  const out: Record<string, string> = {
    "--st-hdr-bg": bg,
    "--st-hdr-bd": resolveRefs(header.border, vars),
  };
  if (contrast(bg, "#ffffff") >= 4.5) {
    Object.assign(out, darkScopeVars(bg, brand), {
      "--ink": resolveRefs(header.ink, vars),
      "--ink-2": resolveRefs(header.ink2, vars),
    });
  }
  return out;
}

/**
 * The stylesheet for one storefront: theme tokens for light and dark mode,
 * scoped to ``[data-site-theme]`` — the storefront wrapper — so the tenant
 * admin keeps its own look.
 */
export function siteThemeCss(theme: SiteTheme, brandInput: string | null | undefined): string {
  const brand = normaliseBrand(brandInput);
  const scope = `[data-site-theme="${theme.key}"]`;
  // The attribute sits on <html> (set by the edge before the bundle runs,
  // then kept by TenantBrandTheme) and on the storefront wrapper; the dark
  // blocks must match both ``:root.dark[attr]`` and ``:root.dark [attr]``.
  const dark = `:root.dark${scope}, :root.dark ${scope}`;
  const parts = [
    block(scope, modeVars(theme, "light", brand)),
    block(dark, modeVars(theme, "dark", brand)),
    block(`${scope} .st-hdr`, headerVars(theme, "light", brand)),
    block(`:root.dark${scope} .st-hdr, :root.dark ${scope} .st-hdr`, headerVars(theme, "dark", brand)),
  ];
  if (theme.heroDark) {
    const heroBg = mix("#121211", brand, 0.1);
    parts.push(block(`${scope} .st-top, ${scope} .st-cta`, darkScopeVars(heroBg, brand)));
  }
  return parts.join("\n");
}

/**
 * The attributes that switch a document to a theme and its section
 * layouts — written on <html> by the edge (no flash of the original look
 * on first load) and kept there by ``TenantBrandTheme``.
 */
export function siteThemeAttributes(
  theme: SiteTheme,
  chosen: Partial<Record<string, string>> | null | undefined,
): Record<string, string> {
  const v = effectiveSectionVariants(theme, chosen);
  return {
    "data-site-theme": theme.key,
    "data-hero": v.hero,
    "data-steps": v.steps,
    "data-benefits": v.benefits,
    "data-page-hero": v.page_hero,
  };
}
